'use client';

import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useCareStateDomain } from '@/entities/care-state-domain';
import { analytics, ANALYTICS_EVENTS } from '@/shared/lib';

import { PROPOSED_ACTIONS_FIRST_ACTION_ANCHOR } from '@/app/hints';

import {
  useProposedActionExecutor,
  useProposedActionMutations,
  useProposedActionsPlan,
  type EnrichedProposedAction,
} from '../model';
import { ProposedActionChecklistRow } from './ProposedActionChecklistRow';

const AUTO_EXECUTE_ON_APPROVE = new Set([
  'draft_prescription',
  'draft_existing_lab',
  'send_follow_up',
]);

export function ProposedActionsPlanView({ emptyState }: { emptyState?: ReactNode } = {}) {
  const { actions, isEmpty, isLoading } = useProposedActionsPlan();
  const { patientUserId } = useCareStateDomain();
  const mutations = useProposedActionMutations();
  const { execute, revise, hasClientExecution } = useProposedActionExecutor();
  const [activeMutation, setActiveMutation] = useState<{
    actionId: string;
    kind: 'approve' | 'reject';
  } | null>(null);

  const pendingActions = useMemo(
    () => actions.filter(({ action }) => action.decision === 'Pending'),
    [actions]
  );

  const approvedActions = useMemo(
    () => actions.filter(({ action }) => action.decision !== 'Pending'),
    [actions]
  );

  const handleApprove = useCallback(
    async (item: EnrichedProposedAction) => {
      setActiveMutation({ actionId: item.action.external_id, kind: 'approve' });
      const autoExecuted = AUTO_EXECUTE_ON_APPROVE.has(item.action.payload.tool_name);
      try {
        await mutations.approve(item.action.external_id);
        void analytics.track({
          name: ANALYTICS_EVENTS.copilot.proposedActionApproved,
          context: { actor: 'clinician', patientUserId },
          properties: {
            externalId: item.action.external_id,
            label: item.action.title,
            toolName: item.action.payload.tool_name,
            autoExecuted,
          },
        });
        if (autoExecuted) {
          execute(item.action);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to approve action';
        toast.error('Cannot approve action', { description: message });
      } finally {
        setActiveMutation(null);
      }
    },
    [mutations, execute, patientUserId]
  );

  const handleReject = useCallback(
    async (item: EnrichedProposedAction, reason: string) => {
      setActiveMutation({ actionId: item.action.external_id, kind: 'reject' });
      try {
        await mutations.reject(item.action.external_id, reason);
        void analytics.track({
          name: ANALYTICS_EVENTS.copilot.proposedActionRejected,
          context: { actor: 'clinician', patientUserId },
          properties: {
            externalId: item.action.external_id,
            label: item.action.title,
            toolName: item.action.payload.tool_name,
            hasReason: reason.trim().length > 0,
          },
        });
      } finally {
        setActiveMutation(null);
      }
    },
    [mutations, patientUserId]
  );

  const handleExecute = useCallback(
    (item: EnrichedProposedAction) => {
      void analytics.track({
        name: ANALYTICS_EVENTS.copilot.proposedActionExecuted,
        context: { actor: 'clinician', patientUserId },
        properties: {
          externalId: item.action.external_id,
          label: item.action.title,
          toolName: item.action.payload.tool_name,
        },
      });
      execute(item.action);
    },
    [execute, patientUserId]
  );

  const handleRevise = useCallback(
    (item: EnrichedProposedAction, reason: string) => {
      try {
        revise(item.action, reason);
        void analytics.track({
          name: ANALYTICS_EVENTS.copilot.proposedActionRevisionStarted,
          context: { actor: 'clinician', patientUserId },
          properties: {
            externalId: item.action.external_id,
            label: item.action.title,
            toolName: item.action.payload.tool_name,
            hasReason: reason.trim().length > 0,
          },
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to start revision';
        toast.error('Cannot revise action', { description: message });
      }
    },
    [patientUserId, revise]
  );

  const actionsById = useMemo(
    () => new Map(actions.map((item) => [item.action.external_id, item])),
    [actions]
  );

  const handleSwitchAlternative = useCallback(
    (externalId: string, alternativePayloadId: string) => {
      const item = actionsById.get(externalId);
      if (item) {
        void analytics.track({
          name: ANALYTICS_EVENTS.copilot.proposedActionAlternativeSwitched,
          context: { actor: 'clinician', patientUserId },
          properties: {
            externalId,
            label: item.action.title,
            toolName: item.action.payload.tool_name,
            alternativePayloadId,
          },
        });
      }
      void mutations.switchAlternative(externalId, alternativePayloadId);
    },
    [mutations, actionsById, patientUserId]
  );

  const orderedActions = useMemo(
    () => [...pendingActions, ...approvedActions],
    [pendingActions, approvedActions]
  );

  const renderActionRow = useCallback(
    (item: EnrichedProposedAction, index: number) => {
      const isPending = item.action.decision === 'Pending';
      const clientExecutable = hasClientExecution(item.action.payload.tool_name);
      const isApproving =
        activeMutation?.actionId === item.action.external_id && activeMutation.kind === 'approve';
      const isRejecting =
        activeMutation?.actionId === item.action.external_id && activeMutation.kind === 'reject';

      return (
        <ProposedActionChecklistRow
          key={item.action.external_id}
          item={item}
          index={index}
          onApprove={
            isPending
              ? (nextItem) => {
                  void handleApprove(nextItem);
                }
              : undefined
          }
          onReject={(nextItem, reason) => {
            void handleReject(nextItem, reason);
          }}
          onRevise={
            clientExecutable && item.action.payload.tool_name !== 'send_follow_up'
              ? (nextItem, reason) => {
                  handleRevise(nextItem, reason);
                }
              : undefined
          }
          onExecute={!isPending && clientExecutable ? handleExecute : undefined}
          onSwitchAlternative={handleSwitchAlternative}
          isApproving={isApproving}
          isRejecting={isRejecting}
          hintAnchor={index === 0 ? PROPOSED_ACTIONS_FIRST_ACTION_ANCHOR : undefined}
        />
      );
    },
    [
      activeMutation,
      handleApprove,
      handleExecute,
      handleReject,
      handleRevise,
      handleSwitchAlternative,
      hasClientExecution,
    ]
  );

  if (isLoading || isEmpty) {
    return emptyState ?? null;
  }

  return (
    <section className="pb-1">
      <div className="max-h-[320px] overflow-y-auto">
        {orderedActions.map((item, index) => renderActionRow(item, index))}
      </div>
    </section>
  );
}
